import moment from 'moment'

import { SHOULD_NOT_SEE_LANDING_PAGE } from 'src/constants/landing_page_cookie'
import { IWindowJsEnv } from 'src/interfaces/index'
import { ILocationShape } from 'src/interfaces/location'

import { cleanShapeObject } from 'src/utils/location'
import { reportError } from 'src/utils/reporting/report-errors'
import * as storageWrapper from 'src/utils/storage_wrapper/storage_wrapper'

const _window = window as unknown as IWindowJsEnv
const jsEnv = _window.js_env
const USER_ANNOYED_AT = 'userAnnoyedAt'
export const LAST_SELECTED_PROFILE_ID_STORAGE_KEY = 'lastSelectedProfileId'

export class StoredProfileHelper {
  public static storeCurrentProfileId (id: string | null): void {
    storageWrapper.setItem(LAST_SELECTED_PROFILE_ID_STORAGE_KEY, id)
  }

  public static getCurrentProfileId (): string | null {
    return storageWrapper.getItem(LAST_SELECTED_PROFILE_ID_STORAGE_KEY)
  }
}

export const isReadyToAnnoyUser = (): boolean => {
  const userAnnoyedAt = storageWrapper.getItem(USER_ANNOYED_AT)
  if (userAnnoyedAt === null) {
    return true
  }

  const dayDifference = moment().diff(userAnnoyedAt, 'seconds')
  return dayDifference > jsEnv.userAnnoyer.timeBetweenModals
}

export const setUserAnnoyedAt = (): void => {
  storageWrapper.setItem(USER_ANNOYED_AT, moment().format())
}

// for legacy reason, we keep it as "address" since this was the name in the past
// and we see no reason to migrte a string name. Address, in this case = location.
export const LAST_ADDRESS_STORAGE_KEY = 'lastAddress'

export class StoredLocationHelper {
  public static getLastLocation (): ILocationShape | undefined {
    return this.parseStoredLastLocation()
  }

  // stores a location as last location
  public static storeLastLocation (locationShape: ILocationShape): void {
    const jsonShape = JSON.stringify(locationShape, (key, value) =>
      key === '__typename' ? undefined : value
    )

    storageWrapper.setItem(LAST_ADDRESS_STORAGE_KEY, jsonShape)
    // Setting a flag-cookie that indicates, that there is a location in the local storage
    // is important for our backend to know to redirect a user to the landing page or not.
    // DO NOT REMOVE TIS LINE. see !4028
    storageWrapper.setCookieItem(SHOULD_NOT_SEE_LANDING_PAGE, true)
  }

  // removes any stored last location
  public static removeLastLocation (): void {
    storageWrapper.removeItem(LAST_ADDRESS_STORAGE_KEY)
    // Removing a flag-cookie that indicates, that there is a location in the local storage
    // is important for our backend to know to redirect a user to the landing page or not.
    // DO NOT REMOVE TIS LINE. see !4028
    storageWrapper.removeCookieItem(SHOULD_NOT_SEE_LANDING_PAGE)
  }

  private static getStoredLastLocation (): string | null {
    return storageWrapper.getItem(LAST_ADDRESS_STORAGE_KEY)
  }

  private static isLocationValid (
    location: unknown
  ): location is ILocationShape {
    if (typeof location !== 'object' || location === null) {
      return false
    }

    const loc = location as Record<string, unknown>

    // id, name, slug must be strings
    if (typeof loc.id !== 'string') return false
    if (typeof loc.name !== 'string') return false
    if (typeof loc.slug !== 'string') return false

    // center must be an object with numeric latitude and longitude
    const center = loc.center as Record<string, unknown> | undefined
    if (!center || typeof center !== 'object') return false
    if (
      typeof center.latitude !== 'number' ||
      typeof center.longitude !== 'number'
    ) {
      return false
    }

    // boundingBox must have northEast and southWest with numeric latitude/longitude
    const bb = loc.boundingBox as Record<string, unknown> | undefined
    if (!bb || typeof bb !== 'object') return false
    const ne = bb.northEast as Record<string, unknown> | undefined
    const sw = bb.southWest as Record<string, unknown> | undefined
    if (!ne || !sw) return false
    if (typeof ne.latitude !== 'number' || typeof ne.longitude !== 'number') {
      return false
    }
    if (typeof sw.latitude !== 'number' || typeof sw.longitude !== 'number') {
      return false
    }

    return true
  }

  private static parseStoredLastLocation (): ILocationShape | undefined {
    const location = this.getStoredLastLocation()

    if (!location) {
      return undefined
    }

    let parsedLocation

    try {
      parsedLocation = JSON.parse(location)
    } catch (error) {
      this.removeLastLocation()
      reportError(
        'Unable to JSON.parse stored last location - deleted storage now',
        {
          error,
          location
        }
      )
      return undefined
    }

    /*  ensure location has the correct format
     *  since the id is now required we can remove any
     *  old stored locations without id
     */

    if (!this.isLocationValid(parsedLocation)) {
      this.removeLastLocation()
      return undefined
    }

    return cleanShapeObject(parsedLocation)
  }
}
